import {
  LiquidSecurityError,
  assertLiquidTemplateIsSafe,
} from "@hightouch/sdk/lib/liquid-template/security";
// This module is the ONLY place allowed to import the engine directly —
// everything else must go through this wrapper so the fs lockdown and the
// render-time RCE gate below can't be skipped.
import {
  Liquid as _Liquid,
  Context,
  type FilterImplOptions,
  type Template,
  Tokenizer,
  TypeGuards,
  type TagToken,
  type Token,
  type TopLevelToken,
  type Value,
  type Expression,
  type Hash,
  type PropertyAccessToken,
  type QuotedToken,
  type RangeToken,
  type Emitter,
  type Tag,
  // biome-ignore lint/style/noRestrictedImports: we need to import liquidjs to wrap it and prevent all other code from using it.
} from "liquidjs";
// `IdentifierToken` is the one token sub-type liquidjs 10 still does not
// re-export from its main entry point (the others above are), so it needs a deep
// import. Reachable at runtime only via the `TypeGuards.isWordToken` predicate.
import type { IdentifierToken } from "liquidjs/dist/tokens/identifier-token";
// `TagImplOptions` moved off the main entry point in liquidjs 10 (its adapter
// wraps an options-style tag into a Tag subclass), so it also needs a deep import.
import type { TagImplOptions } from "liquidjs/dist/template/tag-options-adapter";

// liquidjs 10 dropped the v9 `TagImpl` interface. A registered options-style tag
// executes with the adapter-built `Tag` instance as `this`, carrying the options'
// own fields — this alias is that `this` type.
type TagImpl = Tag & TagImplOptions;

// This can be removed once https://github.com/hightouchio/hightouch/pull/21735 is merged.
type LiquidOptions = ConstructorParameters<typeof _Liquid>[0];

/**
 * Optional sink for render-time RCE-pattern detections, injected by the backend.
 *
 * The detection itself ({@link assertLiquidTemplateIsSafe}) is pure and
 * frontend-safe. Reporting it to Datadog needs the backend logger, whose
 * dependency graph (pino, async-storage, tracer, …) must NOT be pulled into
 * this module: the shared `Liquid` wrapper is reachable from the frontend's
 * type graph (via `@hightouch/sdk/lib/liquid-template/*`), so a static logger
 * import here drags backend-only code into the `packages/app` typecheck and
 * breaks it. Backend entrypoints register a reporter via
 * {@link setLiquidRceReporter}; the frontend leaves it unset (no-op).
 */
export type LiquidSecurityReporter = (
  error: LiquidSecurityError,
  template: string,
) => void;

let liquidRceReporter: LiquidSecurityReporter | undefined;

export function setLiquidRceReporter(reporter: LiquidSecurityReporter): void {
  liquidRceReporter = reporter;
}

/**
 * Optional synchronous predicate gating whether the render-time RCE check runs,
 * injected by the backend (driven by the `liquidRceRenderGateEnabled`
 * LaunchDarkly flag).
 *
 * Kept as an injected hook — not a direct LaunchDarkly read — for two reasons:
 * `parse` is synchronous while `getFeatureFlag` is async (the backend returns a
 * cached value it refreshes out of band), and the LD client must NOT be imported
 * into this frontend-reachable module (same constraint as the logger above).
 *
 * When unset (the frontend, or any path that hasn't opted in) the gate is OFF —
 * i.e. the wrapper behaves exactly as it did before this gate existed.
 * Enforcement is strictly opt-in.
 */
let liquidRceGateEnabledResolver: (() => boolean) | undefined;

export function setLiquidRceGateEnabled(
  resolver: (() => boolean) | undefined,
): void {
  liquidRceGateEnabledResolver = resolver;
}

/**
 * Run the render-time RCE gate exactly as the patched `parse` below does:
 * consult the injected enable resolver, assert the template is safe, report a
 * detection to the injected reporter, rethrow.
 *
 * Scope note: this gate covers engines built from THIS wrapper class (CEP,
 * alerts, LLM prompts, formkit) — every string render there passes through the
 * patched `parse`. The sdk sync engines (`sdk/lib/liquid-template/liquid.ts`)
 * are built directly on liquidjs 10 and do not route through this gate; their
 * mitigation for the v9-era `| valueOf` chain is the engine itself — v10 with
 * `ownPropertyOnly: true` closes the prototype-traversal primitive the chain
 * needed. The gate survives as an LD-armed (`liquidRceRenderGateEnabled`),
 * off-by-default pattern blocker plus attempt telemetry for the wrapper
 * surfaces.
 */
export function runLiquidRceGate(html: string): void {
  if (liquidRceGateEnabledResolver?.()) {
    try {
      assertLiquidTemplateIsSafe(html);
    } catch (error) {
      if (error instanceof LiquidSecurityError) {
        liquidRceReporter?.(error, html);
      }
      throw error;
    }
  }
}

export class Liquid extends _Liquid {
  constructor(options?: LiquidOptions) {
    super({
      ...options,

      // Relative includes ({% include %}/{% render %} with a relative path) are
      // the only feature that drives the engine to touch the fs adapter, which is
      // locked down below — so this is both the security-correct default and an
      // exact match for liquidjs 9, which had no such option at all. On 10+ it
      // defaults to `true` and the engine console.warns once per construction
      // when the fs adapter lacks dirname/sep (ours intentionally omits them),
      // which at sync volume floods stderr as errors. Mirrors the v10 engines in
      // sdk/lib/liquid-template/liquid.ts.
      relativeReference: false,

      // Don't allow templates to access file system.
      // See https://www.hacefresko.com/posts/liquidjs-ssti-to-arbitrary-file-read
      // and https://github.com/harttle/liquidjs/pull/138.
      fs: {
        readFileSync(filepath) {
          throw new Error(
            "Unauthorized attempt to access path `" +
              filepath +
              "` via Liquid template",
          );
        },
        async readFile(filepath) {
          throw new Error(
            "Unauthorized attempt to access path `" +
              filepath +
              "` via Liquid template",
          );
        },
        existsSync(filepath) {
          throw new Error(
            "Unauthorized attempt to access path `" +
              filepath +
              "` via Liquid template",
          );
        },
        async exists(filepath) {
          throw new Error(
            "Unauthorized attempt to access path `" +
              filepath +
              "` via Liquid template",
          );
        },
        // `contains` exists on the FS interface as of v9.28.3
        // (https://github.com/harttle/liquidjs/commit/aebeae9e1bbb8472af7788dfd09a08cb6de58e1c)
        // and so is available on the 10.x engine this wraps. Left unimplemented
        // deliberately: omitting it makes the loader assume "contained" and fall
        // through to `existsSync`/`readFileSync`, which already throw, so adding
        // it would not change behavior.
        // contains(root, file) {
        //   throw new Error(
        //     "Unauthorized attempt to access path `" +
        //       root +
        //       file +
        //       "` via Liquid template",
        //   );
        // },
        resolve(dir, file, ext) {
          throw new Error(
            "Unauthorized attempt to access file `" +
              file +
              ext +
              "` in directory `" +
              dir +
              "` via Liquid template",
          );
        },
      },
    });

    // Render-time gate for the LiquidJS `| valueOf` RCE seed pattern
    // (Dependabot alert #1819). Patched at the instance level — not via a
    // class-level `override` — because the frontend's tsconfig sets
    // `noImplicitOverride: true`, which forces tsgo to fully analyze the
    // upstream Liquid class type and OOMs on the `packages/app/tsconfig.json`
    // check. Instance shadowing covers every caller (shared getEngine factory,
    // destination-local shadow factories, ad-hoc `new Liquid()` callers)
    // without triggering subclass-relationship analysis.
    //
    // `parse` is the single string→template chokepoint, so gating it alone
    // covers every render path: parseAndRender / parseAndRenderSync both
    // delegate to the internal `_parseAndRender`, which calls `this.parse(html)`
    // (verified in liquidjs v9 and v10), and parseFile routes through
    // `this.parse` as well (its fs access is already blocked above).
    // render() / renderSync() take an already-parsed Template[], not a string.
    // Callers that render CACHED ASTs (sdk render-cache.ts) skip parse() on a
    // hit and therefore call {@link runLiquidRceGate} per render themselves.
    //
    // The check is OFF unless the backend has injected an enable resolver that
    // returns true (the `liquidRceRenderGateEnabled` LD flag). Unset → off, so a
    // fresh deploy / LD outage / the frontend all preserve pre-existing
    // behavior; enforcement is strictly opt-in. The detection is the pure,
    // frontend-safe `assertLiquidTemplateIsSafe`; the loud Datadog log is
    // delegated to a backend-registered reporter so the backend logger never
    // enters this frontend-reachable module's graph.
    const origParse = this.parse.bind(this);
    this.parse = (html: string, filepath?: string) => {
      runLiquidRceGate(html);
      return origParse(html, filepath);
    };
  }
}

export type {
  FilterImplOptions,
  TagToken,
  Template,
  Token,
  TopLevelToken,
  PropertyAccessToken,
  IdentifierToken,
  QuotedToken,
  RangeToken,
  Value,
  Expression,
  Hash,
  // Custom-tag authoring surface. Re-exported (rather than deep-imported at the call
  // site) so registering a tag doesn't need a `liquidjs` import, which the
  // noRestrictedImports rule reserves for this wrapper.
  Emitter,
  TagImpl,
  TagImplOptions,
};
export { Context, Tokenizer, TypeGuards };
